import { useEffect, useState } from 'react';

/**
 * Hook that disables tooltips during scrolling to prevent them from appearing
 * at incorrect positions or lingering when elements move out of view.
 *
 * @param {number} delay - Time in milliseconds to wait after scrolling stops before re-enabling tooltips
 * @returns {boolean} - Whether tooltips should be force-closed
 */
const useScrollAwareTooltip = (delay = 100): boolean => {
  const [forceCloseTooltip, setForceCloseTooltip] = useState(false);

  useEffect(() => {
    let scrollTimeout: ReturnType<typeof setTimeout> | undefined;

    const handleScroll = () => {
      setForceCloseTooltip(true);

      // Clear existing timeout to prevent premature re-enable
      if (scrollTimeout) clearTimeout(scrollTimeout);

      // Only re-enable tooltip after scrolling has stopped
      scrollTimeout = setTimeout(() => {
        setForceCloseTooltip(false);
      }, delay);
    };

    // Listen to scroll events on all possible scroll containers
    document.addEventListener('scroll', handleScroll, { capture: true });

    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [delay]);

  return forceCloseTooltip;
};

export { useScrollAwareTooltip };
